/**
 * UTM Traffic Report — super-admin view of where visitors come from.
 * Aggregates the last N days from `utm_visits` collection (server-side, IP anonymised).
 */
import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Compass, Globe2, MousePointerClick, Megaphone, ArrowUpRight, Loader2, TrendingUp } from "lucide-react";
import { api } from "@/lib/api";
import { DashboardLayout } from "@/components/layout/DashboardLayout";

interface Row { name: string; visits: number; }

interface Report {
  days: number;
  total_visits: number;
  by_day: { day: string; visits: number }[];
  top_sources: Row[];
  top_mediums: Row[];
  top_campaigns: Row[];
  top_referrers: Row[];
  top_pages: Row[];
}

const RANGES = [
  { label: "7d", days: 7 },
  { label: "30d", days: 30 },
  { label: "90d", days: 90 },
];

const Sparkline: React.FC<{ data: { day: string; visits: number }[] }> = ({ data }) => {
  if (!data || data.length === 0) return null;
  const max = Math.max(1, ...data.map((d) => d.visits));
  const w = 640, h = 60, step = w / Math.max(1, data.length - 1);
  const points = data.map((d, i) => `${i * step},${h - (d.visits / max) * (h - 6) - 3}`).join(" ");
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-16" preserveAspectRatio="none">
      <polyline points={points} fill="none" stroke="var(--sb-saffron)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      <polygon points={`0,${h} ${points} ${w},${h}`} fill="var(--sb-saffron)" opacity="0.10" />
    </svg>
  );
};

const RankedList: React.FC<{ title: string; icon: React.ReactNode; rows: Row[]; total: number; empty?: string; testId?: string }> = ({ title, icon, rows, total, empty, testId }) => (
  <div className="sg-card p-5" data-testid={testId}>
    <div className="flex items-center gap-2 mb-4">
      <div className="h-8 w-8 rounded-lg bg-[color:var(--sb-saffron)]/10 flex items-center justify-center text-[color:var(--sb-saffron)]">{icon}</div>
      <div className="font-display font-bold text-sm">{title}</div>
    </div>
    {rows.length === 0 ? (
      <div className="text-xs text-[color:var(--sg-muted)] py-6 text-center">{empty || "No data yet."}</div>
    ) : (
      <div className="space-y-2">
        {rows.map((r) => {
          const pct = total > 0 ? Math.round((r.visits / total) * 100) : 0;
          return (
            <div key={r.name} className="text-xs">
              <div className="flex items-center justify-between mb-0.5">
                <div className="truncate flex-1 font-medium">{r.name}</div>
                <div className="text-[color:var(--sg-muted)] ml-2 tabular-nums">{r.visits.toLocaleString()} · {pct}%</div>
              </div>
              <div className="h-1.5 rounded-full bg-[color:var(--sg-muted-bg)] overflow-hidden">
                <div className="h-full bg-[color:var(--sb-saffron)]" style={{ width: `${Math.max(2, pct)}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    )}
  </div>
);

export const UtmReport: React.FC = () => {
  const [days, setDays] = useState(30);
  const q = useQuery<Report>({
    queryKey: ["utm-report", days],
    queryFn: async () => (await api.get(`/analytics/utm-report?days=${days}`)).data,
  });

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto p-6 space-y-6" data-testid="utm-report-page">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-display text-3xl font-bold tracking-tight flex items-center gap-2">
              <TrendingUp className="h-6 w-6 text-[color:var(--sb-saffron)]" /> UTM Traffic Report
            </h1>
            <p className="text-sm text-[color:var(--sg-muted)] mt-1 max-w-2xl">
              First-touch visits across every SociBaba surface — anonymised at capture (hashed IP, no PII). Perfect for measuring campaigns and ad spend attribution.
            </p>
          </div>
          <div className="flex gap-1 items-center rounded-full border border-[color:var(--sg-border)] p-1">
            {RANGES.map((r) => (
              <button
                key={r.days}
                onClick={() => setDays(r.days)}
                data-testid={`utm-range-${r.label}`}
                className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                  days === r.days ? "bg-[color:var(--sg-fg)] text-white" : "text-[color:var(--sg-muted)] hover:text-[color:var(--sg-fg)]"
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>

        {q.isLoading ? (
          <div className="sg-card p-12 text-center text-sm text-[color:var(--sg-muted)]">
            <Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Loading traffic data…
          </div>
        ) : (
          <>
            <div className="sg-card p-6" data-testid="utm-overview">
              <div className="flex items-baseline gap-4 flex-wrap">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[color:var(--sg-muted)]">Total visits · last {q.data?.days} days</div>
                  <div className="font-display text-4xl font-bold text-[color:var(--sg-fg)] tabular-nums" data-testid="utm-total-visits">
                    {(q.data?.total_visits || 0).toLocaleString()}
                  </div>
                </div>
                <div className="flex-1 min-w-[240px]">
                  <Sparkline data={q.data?.by_day || []} />
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              <RankedList testId="utm-sources" title="Top sources" icon={<Compass className="h-4 w-4" />} rows={q.data?.top_sources || []} total={q.data?.total_visits || 0} empty="No campaign traffic yet. Share your UTM links to see this fill up." />
              <RankedList testId="utm-mediums" title="Top mediums" icon={<MousePointerClick className="h-4 w-4" />} rows={q.data?.top_mediums || []} total={q.data?.total_visits || 0} />
              <RankedList testId="utm-campaigns" title="Top campaigns" icon={<Megaphone className="h-4 w-4" />} rows={q.data?.top_campaigns || []} total={q.data?.total_visits || 0} />
              <RankedList testId="utm-referrers" title="Top referrers" icon={<Globe2 className="h-4 w-4" />} rows={q.data?.top_referrers || []} total={q.data?.total_visits || 0} />
              <RankedList testId="utm-pages" title="Top landing pages" icon={<ArrowUpRight className="h-4 w-4" />} rows={q.data?.top_pages || []} total={q.data?.total_visits || 0} />
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default UtmReport;
